import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CustomSnackbar from "../SnackBar";
import useSnackbar from "../SnackbarHook";

import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import {
  Button as MuiButton,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
  CircularProgress,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import { Edit, Delete, Add, AttachFile, Close } from "@mui/icons-material";

import {
  fetchMeetings,
  createMeeting,
  updateMeeting,
  deleteMeeting,
  Meeting,
  MeetingAttachment,
  openMeetingForm,
  setMeeting,
  sendMeetingInvites,
} from "../../../redux/slices/meeting";
import { uploadFile } from "../../../redux/slices/file";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import useAuth from "../../../hooks/useAuth";
import { usePermission } from "../../../hooks/usePermission";
import { format } from "date-fns";
import { DateTimePicker } from "@mui/x-date-pickers";
import {
  fetchMeetingInvitesByMeetingId,
  patchInvite,
} from "../../../redux/slices/meetingInvite";
import MeetingInvite from "./MeetingInvite";
import { baseUrl } from "../../../config";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Button = styled(MuiButton)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Aihe on pakollinen"),
  date: Yup.string().required("Päivämäärä on pakollinen"),
});

interface MeetingFormValues {
  title: string;
  date: string;
  agenda: string;
  submit?: string;
  place: string;
  type: string;
  associationId: number | null;
}

function MeetingForm({
  onComplete,
  onSaveAndCreateInvite,
  setOpenSnackbar,
  setSnackbarMessage,
  setSnackbarSeverity,
}: {
  onComplete: (success: boolean) => void;
  onSaveAndCreateInvite: (_meetingId: number) => void;
  setOpenSnackbar: (isOpen: boolean) => void;
  setSnackbarMessage: (message: string) => void;
  setSnackbarSeverity: (
    severity: "success" | "error" | "info" | "warning"
  ) => void;
}) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { meeting } = useAppSelector((state) => state.meeting);
  const esRef = useRef<EventSource | null>(null);
  const finishedIdsRef = useRef<Set<number>>(new Set()); // de-dup
  const expectedCountRef = useRef<number>(0);

  const associationId = currentUser?.activeAssociation?.id;

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [pendingInviteResult, setPendingInviteResult] =
    useState<Meeting | null>(null);
  const [sendingInvites, setSendingInvites] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const inviteSectionRef = useRef<HTMLDivElement>(null);
  const [attachments, setAttachments] = useState<MeetingAttachment[]>(
    meeting?.attachments || []
  );
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // Sync local attachment list when the active meeting changes (e.g. switching edits)
  useEffect(() => {
    setAttachments(meeting?.attachments || []);
  }, [meeting?.id]);

  const handleAttachmentUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingAttachment(true);
    try {
      const result = await dispatch(
        uploadFile({ file, associationId })
      ).unwrap();
      setAttachments((prev) => [
        ...prev,
        {
          id: result.id,
          originalname: result.originalname,
          filename: result.filename,
        },
      ]);
    } catch {
      setSnackbarMessage("Liitteen lisääminen epäonnistui.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  // const finalRefresh = async (meetingId: number) => {
  //   // canonical state from DB
  //   console.log("does it call this");
  //   await dispatch(fetchMeetingInvitesByMeetingId(meetingId));
  // };

  const resetBatchCounters = (recipients: number) => {
    finishedIdsRef.current = new Set();
    expectedCountRef.current = recipients;
  };

  const maybeFinalize = async (meetingId: number, es: EventSource) => {
    if (finishedIdsRef.current.size >= expectedCountRef.current) {
      await dispatch(fetchMeetingInvitesByMeetingId(meetingId)); // final DB sync
      es.close();
      if (esRef.current === es) esRef.current = null;
    }
  };

  // open SSE for a batch
  const openInviteStream = async (
    batchId: string,
    streamUrl: string,
    meetingId: number,
    recipients: number
  ) => {
    resetBatchCounters(recipients);

    // Load snapshot first
    await dispatch(fetchMeetingInvitesByMeetingId(meetingId));

    // Open SSE
    if (esRef.current) esRef.current.close();
    const url = streamUrl.startsWith("http")
      ? streamUrl
      : `${baseUrl}/meeting-invites/batch/${batchId}/stream`;
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener("open", () => {
      setSnackbarMessage("Kutsujen lähetys käynnissä…");
      setSnackbarSeverity("info");
      setOpenSnackbar(true);
    });

    // Live patch for progress
    es.addEventListener("progress", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.inviteId) return;

      // Patch UI
      dispatch(
        patchInvite({
          inviteId: msg.inviteId,
          updates: {
            status: msg.status,
            pct: msg.pct,
            ...(msg.fileId ? { file: { id: msg.fileId } } : {}),
          },
        })
      );

      // Some backends emit "sent" as a progress update; treat that as terminal for email or post
      if (
        (msg.source === "email" || msg.source === "post") &&
        msg.status === "sent"
      ) {
        if (!finishedIdsRef.current.has(msg.inviteId)) {
          finishedIdsRef.current.add(msg.inviteId);
          maybeFinalize(meetingId, es);
        }
      }
    });

    // Count parent (email or post) completion as terminal; ignore PDF child jobs
    es.addEventListener("completed", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.inviteId) return;

      // Ignore file child completion — only email/post parent jobs are terminal
      if (msg.source === "file") return;

      // Patch to sent (if not already)
      dispatch(
        patchInvite({
          inviteId: msg.inviteId,
          updates: {
            status: msg.status ?? "sent",
            sentAt: msg.sentAt ?? new Date().toISOString(),
            pct: 100,
            ...(msg.fileId ? { file: { id: msg.fileId } } : {}),
          },
        })
      );

      if (!finishedIdsRef.current.has(msg.inviteId)) {
        finishedIdsRef.current.add(msg.inviteId);
        maybeFinalize(meetingId, es);
      }
    });

    es.addEventListener("failed", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.inviteId) return;

      // Patch to failed
      dispatch(
        patchInvite({
          inviteId: msg.inviteId,
          updates: {
            status: "failed",
            lastError: msg.reason ?? msg.lastError ?? "Tuntematon virhe",
          },
        })
      );

      if (!finishedIdsRef.current.has(msg.inviteId)) {
        finishedIdsRef.current.add(msg.inviteId);
        maybeFinalize(meetingId, es);
      }
    });

    es.addEventListener("ping", () => {});
    es.addEventListener("error", () => {
      // EventSource will auto-reconnect; optionally log
    });
  };

  const handleSubmit = async (
    values: MeetingFormValues,
    formikHelpers: FormikHelpers<MeetingFormValues>
  ) => {
    const { resetForm, setErrors, setStatus, setSubmitting } = formikHelpers;

    try {
      const payload = {
        ...values,
        associationId,
        attachmentIds: attachments.map((a) => a.id),
      };

      if (meeting?.id !== undefined) {
        await dispatch(
          updateMeeting({ id: meeting.id, updatedMeeting: payload })
        ).unwrap();
      } else {
        await dispatch(createMeeting(payload)).unwrap();
      }

      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);
      dispatch(openMeetingForm(false));
      onComplete(true);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Virhe tallennettaessa kokousta.";

      setErrors({ submit: errorMessage });
      setStatus({ sent: false });
      setSubmitting(false);
      onComplete(false);
    }
  };

  const handleSaveAndRedirect = async (
    values: MeetingFormValues,
    formikHelpers: FormikHelpers<MeetingFormValues>
  ) => {
    const { setErrors, setStatus, setSubmitting } = formikHelpers;

    try {
      const payload = {
        ...values,
        associationId,
        attachmentIds: attachments.map((a) => a.id),
      };

      const result =
        meeting?.id !== undefined
          ? await dispatch(
              updateMeeting({ id: meeting.id, updatedMeeting: payload })
            ).unwrap()
          : await dispatch(createMeeting(payload)).unwrap();

      dispatch(setMeeting(result));

      setStatus({ sent: true });
      setSubmitting(false);

      if (typeof result.id === "number") {
        onSaveAndCreateInvite(result.id);
        setPendingInviteResult(result);
        setInviteDialogOpen(true);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Virhe tallennettaessa kokousta.";

      setErrors({ submit: errorMessage });
      setStatus({ sent: false });
      setSubmitting(false);
      onComplete(false);
    }
  };

  const handleConfirmInvites = async () => {
    if (!pendingInviteResult?.id) return;
    setSendingInvites(true);
    try {
      const { batchId, recipients, streamUrl } = await dispatch(
        sendMeetingInvites({
          associationId,
          meetingId: pendingInviteResult.id,
        })
      ).unwrap();

      setInviteDialogOpen(false);
      setPendingInviteResult(null);

      await openInviteStream(
        batchId,
        streamUrl,
        pendingInviteResult.id,
        recipients
      );

      // Scroll to the invite progress section so the user sees live updates
      setTimeout(() => {
        inviteSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);

      setSnackbarMessage(
        `Kutsujen lähetys aloitettu (${recipients} vastaanottajaa).`
      );
      setSnackbarSeverity("success");
    } catch {
      setSnackbarMessage("Virhe kutsujen lähetyksessä.");
      setSnackbarSeverity("error");
    } finally {
      setSendingInvites(false);
      setOpenSnackbar(true);
    }
  };

  const templateAgenda = `1. Kokouksen avaus.

2. Valitaan kokouksen puheenjohtaja, sihteeri, kaksi pöytäkirjantarkastajaa ja kaksi ääntenlaskijaa.

3. Todetaan kokouksen laillisuus ja päätösvaltaisuus.

4. Hyväksytään esityslista.

5. Esitetään toimintakertomus, tilinpäätös ja tilintarkastajien lausunto.

6. Päätetään tilinpäätöksen vahvistamisesta ja vastuuvapauden myöntämisestä hallitukselle.

7. Vahvistetaan toimintasuunnitelma, tulo- ja menoarvio sekä liittymis- ja jäsenmaksujen suuruudet.

8. Valitaan hallituksen puheenjohtaja.

9. Valitaan hallituksen muut jäsenet.

10. Valitaan tilintarkastaja ja varatilintarkastaja.

11. Käsitellään muut esille tulevat asiat.

12. Kokouksen päättäminen.`;

  // close SSE when component unmounts or when you navigate away
  useEffect(() => {
    return () => {
      if (esRef.current) esRef.current.close();
    };
  }, []);

  if (!associationId) {
    return (
      <Card mb={6}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="30vh"
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Formik<MeetingFormValues>
      initialValues={{
        title: meeting?.title || "",
        date: meeting?.date ?? new Date().toISOString(),
        agenda: meeting?.agenda || templateAgenda,
        place: meeting?.place || "",
        type: meeting?.type || "annual",
        associationId: meeting?.associationId || null,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formikHelpers) => {
        const {
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldValue,
        } = formikHelpers;

        const handleSaveAndRedirectWrapper = () =>
          handleSaveAndRedirect(values, formikHelpers);

        return (
          <>
            <Card mb={6}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {meeting ? "Muokkaa kokousta" : "Luo uusi kokous"}
                </Typography>

                <form onSubmit={handleSubmit}>
                  {/* Section 1: Basic info */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="title"
                        label="Aihe"
                        value={values.title}
                        error={Boolean(touched.title && errors.title)}
                        fullWidth
                        helperText={touched.title && errors.title}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        variant="outlined"
                        my={2}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="type"
                        label="Tyyppi"
                        select
                        value={values.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.type && errors.type)}
                        helperText={touched.type && errors.type}
                        fullWidth
                        variant="outlined"
                        my={2}
                      >
                        <MenuItem value="annual">Vuosikokous</MenuItem>
                        <MenuItem value="extraordinary">Ylimääräinen</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="Päivämäärä ja aika"
                        ampm={false}
                        value={values.date ? new Date(values.date) : null}
                        onChange={(newValue) => {
                          setFieldValue("date", newValue?.toISOString() ?? "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="date"
                            onBlur={handleBlur}
                            error={Boolean(touched.date && errors.date)}
                            helperText={touched.date && errors.date}
                            fullWidth
                            variant="outlined"
                            my={2}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        name="place"
                        label="Paikka"
                        value={values.place}
                        error={Boolean(touched.place && errors.place)}
                        fullWidth
                        helperText={touched.place && errors.place}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        variant="outlined"
                        my={2}
                      />
                    </Grid>
                  </Grid>

                  <Divider my={4} />

                  {/* Section 2: Agenda */}
                  <Typography variant="subtitle1" gutterBottom>
                    Esityslista
                  </Typography>
                  <TextField
                    name="agenda"
                    value={values.agenda}
                    error={Boolean(touched.agenda && errors.agenda)}
                    fullWidth
                    multiline
                    rows={12}
                    helperText={touched.agenda && errors.agenda}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                  />

                  <Divider my={4} />

                  {/* Section 3: Attachments */}
                  <Typography variant="subtitle1" gutterBottom>
                    Liitteet
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    {attachments.map((att) => (
                      <Box
                        key={att.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          py: 0.75,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <AttachFile fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {att.originalname}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveAttachment(att.id)}
                          aria-label="Poista liite"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleAttachmentUpload}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      uploadingAttachment ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Add />
                      )
                    }
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAttachment}
                  >
                    Lisää liite
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Liitteet lähetetään sähköpostikutsujen mukana. Kirjepostia
                    saavat jäsenet eivät saa liitteitä.
                  </Typography>

                  <Divider my={4} />

                  {/* Section 4: Actions */}
                  {errors.submit && (
                    <Typography color="error" mb={2}>
                      {errors.submit}
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      Tallenna
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={isSubmitting}
                      onClick={handleSaveAndRedirectWrapper}
                    >
                      Lähetä kutsut
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => dispatch(openMeetingForm(false))}
                    >
                      Peruuta
                    </Button>
                  </Box>
                </form>
              </CardContent>
            </Card>

            {/* Invite list below form when editing an existing meeting */}
            {meeting?.id && (
              <div ref={inviteSectionRef}>
                <MeetingInvite meetingId={meeting.id} hideHeader />
              </div>
            )}

            {/* Send invites confirmation dialog */}
            <Dialog
              open={inviteDialogOpen}
              onClose={() => !sendingInvites && setInviteDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Lähetä kokouskutsut</DialogTitle>
              <DialogContent>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Kokous
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {pendingInviteResult?.title}
                  </Typography>
                  {pendingInviteResult?.date && (
                    <Typography variant="body2">
                      {format(
                        new Date(pendingInviteResult.date),
                        "dd.MM.yyyy HH:mm"
                      )}
                    </Typography>
                  )}
                  {pendingInviteResult?.place && (
                    <Typography variant="body2">
                      {pendingInviteResult.place}
                    </Typography>
                  )}
                </Box>

                {attachments.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {attachments.length === 1
                      ? "1 liite lähetetään sähköpostikutsujen mukana."
                      : `${attachments.length} liitettä lähetetään sähköpostikutsujen mukana.`}
                  </Alert>
                )}

                <Alert severity="warning">
                  Kirjepostia käyttävät jäsenet saavat vain kutsukirjeen —
                  liitteitä ei lähetetä kirjeitse.
                </Alert>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={() => {
                    setInviteDialogOpen(false);
                    setPendingInviteResult(null);
                  }}
                  disabled={sendingInvites}
                  color="inherit"
                >
                  Peruuta
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmInvites}
                  disabled={sendingInvites}
                  startIcon={
                    sendingInvites ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {sendingInvites ? "Lähetetään..." : "Lähetä kutsut"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        );
      }}
    </Formik>
  );
}

function MeetingList(props: {
  rows: Meeting[];
  handleEditMeeting: (meeting: Meeting | null, isOpen: boolean) => void;
  handleDelete: (meeting: Meeting) => void;
}) {
  const { currentUser } = useAuth();
  const { status = "idle" } = useAppSelector((state) => state.meeting || {});
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  // Check permissions using RBAC
  const { hasPermission: canDeleteMeeting } = usePermission(
    "meeting",
    "delete",
    associationId
  );

  const [sortField, setSortField] = useState<"title" | "date">("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSort = (field: "title" | "date") => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortArrow = (field: "title" | "date") => {
    if (sortField !== field) return "";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  const sortedRows = [...(props.rows || [])].sort((a, b) => {
    const aVal = a[sortField]?.toLowerCase?.() || "";
    const bVal = b[sortField]?.toLowerCase?.() || "";
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const getMeetingTypeLabel = (type: string) => {
    switch (type) {
      case "annual":
        return "Vuosikokous";
      case "extraordinary":
        return "Ylimääräinen";
      default:
        return type;
    }
  };

  const getMeetingStatusChip = (status?: string) => {
    switch (status) {
      case "published":
        return <Chip size="small" label="Julkaistu" color="primary" />;
      case "held":
        return <Chip size="small" label="Pidetty" color="success" />;
      case "cancelled":
        return <Chip size="small" label="Peruttu" color="error" />;
      default:
        return <Chip size="small" label="Luonnos" variant="outlined" />;
    }
  };

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Kokoukset
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => props.handleEditMeeting(null, true)}
        >
          Lisää kokous
        </Button>
      </CardContent>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                onClick={() => handleSort("title")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Aihe{getSortArrow("title")}
              </TableCell>
              <TableCell>Tyyppi</TableCell>
              <TableCell>Paikka</TableCell>
              <TableCell
                onClick={() => handleSort("date")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Päivämäärä{getSortArrow("date")}
              </TableCell>
              <TableCell>Tila</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loading" ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Ei kokouksia
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row: Meeting) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{getMeetingTypeLabel(row.type)}</TableCell>
                  <TableCell>{row.place || "—"}</TableCell>
                  <TableCell>
                    {format(new Date(row.date), "dd.MM.yyyy HH:mm")}
                  </TableCell>
                  <TableCell>{getMeetingStatusChip(row.status)}</TableCell>
                  <TableCell>
                    <Edit
                      style={{ cursor: "pointer" }}
                      onClick={() => props.handleEditMeeting(row, true)}
                    />{" "}
                    {canDeleteMeeting ? (
                      <Delete
                        style={{ cursor: "pointer" }}
                        onClick={() => props.handleDelete(row)}
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Meetings() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { meetings, open } = useAppSelector((state) => state.meeting);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleSaveAndCreateInvite = (_meetingId: any) => {
    // You might not need this navigation logic if you're handling it with tabs
    // but I'll keep it here for context.
    // navigate(`/meetings/meeting-invites/${meetingId}`, {
    //   state: { meetingId },
    // });
    setSnackbarMessage("Kokous tallennettu ja kutsujen luonti aloitetaan.");
    setSnackbarSeverity("info");
    setOpenSnackbar(true);
  };

  const handleDelete = async (meeting: Meeting) => {
    const confirmed = window.confirm(
      `Haluatko varmasti poistaa kokouksen "${meeting.title}"?`
    );
    if (confirmed && meeting.id) {
      setProgress(100);
      try {
        await dispatch(deleteMeeting(meeting.id));
        setSnackbarMessage("Kokous poistettu onnistuneesti!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } catch (e) {
        setSnackbarMessage("Virhe poistettaessa kokousta.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  const handleEditMeeting = (meeting: Meeting | null, isOpen: boolean) => {
    dispatch(setMeeting(meeting));
    dispatch(openMeetingForm(isOpen));
  };

  useEffect(() => {
    if (associationId) {
      dispatch(fetchMeetings(associationId));
    }
    return () => {
      dispatch(openMeetingForm(false));
    };
  }, [dispatch, associationId]);

  return (
    <React.Fragment>
      <Helmet title="Kokoukset" />
      <Typography variant="h3" gutterBottom display="inline">
        Kokoukset
      </Typography>
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Kokoukset</Typography>
      </Breadcrumbs>

      <Divider my={6} />
      {open ? (
        <MeetingForm
          onComplete={(success: boolean) => {
            setProgress(100);
            if (success) {
              setSnackbarMessage("Kokous tallennettu onnistuneesti!");
              setSnackbarSeverity("success");
            } else {
              setSnackbarMessage("Virhe tallennettaessa kokousta.");
              setSnackbarSeverity("error");
            }
            setOpenSnackbar(true);
          }}
          setOpenSnackbar={setOpenSnackbar}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarSeverity={setSnackbarSeverity}
          onSaveAndCreateInvite={handleSaveAndCreateInvite}
        />
      ) : (
        <MeetingList
          rows={meetings}
          handleEditMeeting={handleEditMeeting}
          handleDelete={handleDelete}
        />
      )}
      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </React.Fragment>
  );
}

export default Meetings;
